import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Box, Button } from '@mui/material';

import {
    loadAssetRequestAssetDetails,
    loadAssetRequestRequestTypeDetails,
    loadAssetRequestStatusDetails
} from '../../../../store/directusService';
import {
    clearParticipantAssetRequests,
    loadAssetRequests
} from '../../../../store/participantService';
import { selectUsersForNames } from '../../../../store/userSelectors';
import { loadUsersMinimalDetails } from '../../../../store/userService';
import { SCREEN_SETTINGS } from '../../../../themes/theme';
import {
    ADVISER,
    hasRole,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../../utils/userRoles';
import LoadingSpinner from '../../../ui/LoadingSpinner';

import AssetRequestTable from './AssetRequestTable';
import CreateAssetRequest from './CreateAssetRequest';
import EditAssetRequest from './EditAssetRequest';

import classes from './assetRequest.module.css';

const acceptedRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];
const editOnClosedRoles = [QUALITY, SUPERUSER];

const AssetRequest = ({ isViewOnly = false }) => {
    const dispatch = useDispatch();
    // LOCAL STATE
    const [rows, setRows] = useState([]);
    const [selectedRow, setSelectedRow] = useState({});
    const [showForm, setShowForm] = useState(false);
    const [formType, setFormType] = useState('');
    const [isDisabled, setIsDisabled] = useState(false);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const participantAssetRequests = useSelector(
        (state) => state.entities.participantService.participantAssetRequests
    );
    const assetRequestStatusDetails = useSelector(
        (state) => state.entities.directusService.assetRequestStatusDetails
    );
    const assetRequestRequest = useSelector(
        (state) => state.entities.directusService.assetRequestRequestTypeDetails
    );
    const assetRequestAsset = useSelector(
        (state) => state.entities.directusService.assetRequestAssetDetails
    );
    const users = useSelector(selectUsersForNames);

    // USE EFFECTS
    useEffect(() => {
        return () => cleanUp();
    }, []);

    useEffect(() => {
        setRows([]);
        if (!currentParticipant?.id) return;
        dispatch(loadAssetRequests(currentParticipant.id));
        !assetRequestStatusDetails.length && dispatch(loadAssetRequestStatusDetails());
        !assetRequestRequest.length && dispatch(loadAssetRequestRequestTypeDetails());
        !assetRequestAsset.length && dispatch(loadAssetRequestAssetDetails());
        const disableOnClosedStatus =
            currentParticipant.status === 'Closed' && !hasRole(editOnClosedRoles, roles);
        setIsDisabled(!hasRole(acceptedRoles, roles) || disableOnClosedStatus || isViewOnly);
    }, [currentParticipant]);

    useEffect(() => {
        if (
            !participantAssetRequests?.length ||
            !assetRequestStatusDetails?.length ||
            !assetRequestRequest?.length ||
            !assetRequestAsset.length
        )
            return;
        if(!currentParticipant.id === participantAssetRequests[0]?.participantId) return;
        const userIds = [
            ...new Set([
                ...participantAssetRequests.map((el) => el.authoriserId),
                ...participantAssetRequests.map((el) => el.adviserId)
            ])
        ];
        const missingUserIds = userIds.filter((el) => !users.find((entry) => entry.id === el)).filter((el) => el);
        if (missingUserIds.length > 0) dispatch(loadUsersMinimalDetails(missingUserIds));
        else setRows(
            participantAssetRequests.map((el) => {
                const authoriser = users.find((entry) => entry.id === el.authoriserId);
                return {
                    ...el,
                    user: authoriser ? `${authoriser.firstName} ${authoriser.lastName}` : '',
                    asset:
                        assetRequestAsset.find((entry) => entry.id === el.assetId)?.name ||
                        el.asset,
                    status:
                        assetRequestStatusDetails.find((entry) => entry.id === el.statusId)?.name ||
                        ''
                };
            })
        );
    }, [
        participantAssetRequests,
        assetRequestStatusDetails,
        assetRequestRequest,
        assetRequestAsset,
        users
    ]);
    //HELPER FUNCTIONS
    const cleanUp = () => {
        dispatch(clearParticipantAssetRequests());
    };

    // EVENT HANDLERS
    const onCreateAssetRequest = () => {
        setFormType('create');
        setShowForm(true);
    };

    const onEditAssetRequest = (row) => {
        setSelectedRow(row);
        setFormType('edit');
        setShowForm(true);
    };

    return (
        <div className={classes.assetRequests} data-testid="asset_requests">
            <h3>Asset Request Details</h3>
            <div className={classes.tableSection}>
                {rows.length < 1 ? (
                    <LoadingSpinner content="No asset requests found" />
                ) : (
                    <AssetRequestTable
                        rows={rows}
                        onEdit={onEditAssetRequest}
                        isDisabled={isDisabled}
                    />
                )}
            </div>
            <div>
                {!showForm && (
                    <Button
                        disabled={isDisabled}
                        color="primary"
                        variant="contained"
                        size={'small'}
                        onClick={onCreateAssetRequest}>
                        Add Asset Request
                        <Box width={SCREEN_SETTINGS.gap.medium} />
                        <AddCircleOutlineIcon />
                    </Button>
                )}
                {showForm && formType === 'create' && (
                    <CreateAssetRequest onClose={() => setShowForm(false)} roles={roles} />
                )}
                {showForm && formType === 'edit' && (
                    <EditAssetRequest
                        onClose={() => setShowForm(false)}
                        roles={roles}
                        row={selectedRow}
                    />
                )}
            </div>
        </div>
    );
};

AssetRequest.propTypes = {
    isViewOnly: PropTypes.bool
};

export default AssetRequest;
